import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    FormControlLabel,
    Switch,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    Stack,
    Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import RouterIcon from '@mui/icons-material/Router';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import api from '../services/httpClient';

const RegistrationRequestsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [showOrphanedOnly, setShowOrphanedOnly] = useState(false);

    useEffect(() => {
        fetchRegistrations();
    }, []);

    const fetchRegistrations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/v1/admin/registrations');
            setRegistrations(response.data);
        } catch (err) {
            console.error('Error fetching registrations:', err);
            setError('Failed to load registration requests');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRegistration = async (registrationId) => {
        try {
            await api.delete(`/v1/admin/registrations/${registrationId}`);
            
            setSuccess('Registration deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedRegistration(null);
            fetchRegistrations();
        } catch (err) {
            console.error('Error deleting registration:', err);
            setError('Failed to delete registration');
        }
    };

    const handleBulkDeleteOrphaned = async () => {
        try {
            const response = await api.delete('/v1/admin/registrations/orphaned');
            
            setSuccess(response.data.message);
            setBulkDeleteDialogOpen(false);
            fetchRegistrations();
        } catch (err) {
            console.error('Error deleting orphaned registrations:', err);
            setError('Failed to delete orphaned registrations');
        }
    };

    const openDeleteDialog = (registration) => {
        setSelectedRegistration(registration);
        setDeleteDialogOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved':
                return 'success';
            case 'pending_approval':
                return 'warning';
            case 'rejected':
                return 'error';
            case 'expired':
                return 'default';
            default:
                return 'info';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Helper function to render registration cards (mobile)
    const renderRegistrationCard = (registration) => (
        <Card 
            key={registration.registrationId} 
            sx={{ 
                mb: 2,
                backgroundColor: registration.isOrphaned ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                border: registration.isOrphaned ? '1px solid rgba(255, 152, 0, 0.3)' : 'none',
            }}
        >
            <CardContent>
                {/* Header with name and status */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RouterIcon sx={{ mr: 1, color: registration.isOrphaned ? 'warning.main' : 'primary.main', fontSize: 32 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            {registration.deviceName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                            {registration.registrationId}
                        </Typography>
                    </Box>
                    <Chip
                        label={registration.status}
                        color={getStatusColor(registration.status)}
                        size="small"
                    />
                </Box>

                {/* Orphaned Warning */}
                {registration.isOrphaned && (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                        Orphaned - Gateway no longer exists
                    </Alert>
                )}

                {/* Location */}
                {registration.deviceLocation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LocationOnIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                        <Typography variant="body2">{registration.deviceLocation}</Typography>
                    </Box>
                )}

                {/* Registration Details */}
                <Stack spacing={1}>
                    <Box>
                        <Typography variant="caption" color="textSecondary">VPN IP</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                            {registration.assignedVpnIp || 'N/A'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary">Source IP</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                            {registration.sourceIp}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary">Requested</Typography>
                        <Typography variant="body2">{formatDate(registration.requestedAt)}</Typography>
                    </Box>
                    {registration.approvedAt && (
                        <Box>
                            <Typography variant="caption" color="textSecondary">Approved</Typography>
                            <Typography variant="body2">{formatDate(registration.approvedAt)}</Typography>
                        </Box>
                    )}
                </Stack>
            </CardContent>
            <Divider />
            <CardActions sx={{ p: 2 }}>
                <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => openDeleteDialog(registration)}
                >
                    Delete
                </Button>
            </CardActions>
        </Card>
    );

    const filteredRegistrations = showOrphanedOnly
        ? registrations.filter(r => r.isOrphaned)
        : registrations;

    const orphanedCount = registrations.filter(r => r.isOrphaned).length;

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                    Registration Requests
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOrphanedOnly}
                                onChange={(e) => setShowOrphanedOnly(e.target.checked)}
                                color="warning"
                            />
                        }
                        label={`Show Orphaned Only (${orphanedCount})`}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchRegistrations}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="large"
                        startIcon={<DeleteSweepIcon />}
                        onClick={() => setBulkDeleteDialogOpen(true)}
                        disabled={orphanedCount === 0}
                    >
                        🧹 Clean All Orphaned ({orphanedCount})
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {orphanedCount > 0 && !showOrphanedOnly && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                    Found {orphanedCount} orphaned registration request(s). These are registrations linked to gateways that no longer exist.
                </Alert>
            )}

            {isMobile ? (
                // Mobile Card View
                <Box>
                    {filteredRegistrations.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {showOrphanedOnly ? 'No orphaned registrations found' : 'No registration requests found'}
                            </Typography>
                        </Paper>
                    ) : (
                        filteredRegistrations.map((registration) => renderRegistrationCard(registration))
                    )}
                </Box>
            ) : (
                // Desktop Table View
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Device Name</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>WireGuard Key</TableCell>
                                <TableCell>VPN IP</TableCell>
                                <TableCell>Source IP</TableCell>
                                <TableCell>Requested</TableCell>
                                <TableCell>Approved</TableCell>
                                <TableCell>Audit Logs</TableCell>
                                <TableCell>Orphaned</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRegistrations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            {showOrphanedOnly ? 'No orphaned registrations found' : 'No registration requests found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRegistrations.map((registration) => (
                                    <TableRow
                                        key={registration.registrationId}
                                        sx={{
                                            backgroundColor: registration.isOrphaned ? 'rgba(255, 152, 0, 0.08)' : 'inherit'
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {registration.deviceName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {registration.registrationId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{registration.deviceLocation || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={registration.status}
                                                color={getStatusColor(registration.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {registration.wireguardPublicKey || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {registration.assignedVpnIp || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {registration.sourceIp}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDate(registration.requestedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDate(registration.approvedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={registration.auditLogCount}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {registration.isOrphaned && (
                                                <Tooltip title="This registration is linked to a deleted gateway">
                                                    <WarningIcon color="warning" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete registration">
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => openDeleteDialog(registration)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Single Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Registration Request?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the registration request for{' '}
                        <strong>{selectedRegistration?.deviceName}</strong>?
                        {selectedRegistration?.auditLogCount > 0 && (
                            <Box component="span" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
                                This will also delete {selectedRegistration.auditLogCount} associated audit log(s).
                            </Box>
                        )}
                        <Box component="span" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                            This action cannot be undone.
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleDeleteRegistration(selectedRegistration?.registrationId)}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Orphaned Confirmation Dialog */}
            <Dialog
                open={bulkDeleteDialogOpen}
                onClose={() => setBulkDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete All Orphaned Registrations?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete all {orphanedCount} orphaned registration request(s)?
                        <Box component="span" sx={{ display: 'block', mt: 2 }}>
                            Orphaned registrations are those linked to gateways that have been deleted.
                            This cleanup operation will:
                        </Box>
                        <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                            <li>Delete {orphanedCount} registration request(s)</li>
                            <li>Delete all associated audit logs</li>
                            <li>Free up database space</li>
                        </Box>
                        <Box component="span" sx={{ display: 'block', mt: 2, color: 'error.main' }}>
                            This action cannot be undone.
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleBulkDeleteOrphaned}
                        color="warning"
                        variant="contained"
                        autoFocus
                        startIcon={<DeleteSweepIcon />}
                    >
                        Delete All Orphaned
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default RegistrationRequestsPage;

